/**
 * Rotulos e tons do modulo de disparos.
 *
 * Records com chave de uniao literal (nao index signature): se um status novo
 * entrar no tipo, o tsc aponta o Record incompleto em vez de renderizar
 * `undefined` na tela. A cor de status nunca viaja sozinha -- todo consumidor
 * usa `Badge`, que leva o texto junto.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import type { RefundPolicy } from '../../types';
import type {
  BroadcastStatus,
  DnsCheck,
  EmailCampaignStatus,
  EmailProviderState,
  NumberQuality,
  OptOutReason,
  PausedReason,
  SegmentFamily,
  SegmentKey,
  SenderKind,
  TemplateApproval,
  TemplateCategory,
  TemplateSector,
} from './types';

/** Todos os segmentos, na ordem em que aparecem (familia, depois temperatura). */
export const SEGMENT_KEYS: readonly SegmentKey[] = [
  'abandonaram_sem_pagar',
  'clicaram_sem_pagar',
  'pagamento_recusado',
  'responderam_sem_fechar',
  'expirados_d15',
  'expirados_d30',
  'expirados_d60',
];

export function isSegmentKey(valor: string): valor is SegmentKey {
  return SEGMENT_KEYS.some((k) => k === valor);
}

/** Mesmos rotulos do mock, para telas que so tem a chave (campanha de e-mail, radar). */
export const SEGMENT_LABEL: Readonly<Record<SegmentKey, string>> = {
  abandonaram_sem_pagar: 'Abandonaram e não pagaram (régua encerrada)',
  clicaram_sem_pagar: 'Clicaram no botão e não pagaram',
  pagamento_recusado: 'Pagamento recusado ou Pix vencido',
  responderam_sem_fechar: 'Responderam e a vendedora não fechou',
  expirados_d15: 'Expirados há 15 a 29 dias',
  expirados_d30: 'Expirados há 30 a 59 dias',
  expirados_d60: 'Expirados há 60 dias ou mais',
};

/** Rotulo curto para chips, colunas estreitas e nome sugerido do envio. */
export const SEGMENT_SHORT_LABEL: Readonly<Record<SegmentKey, string>> = {
  abandonaram_sem_pagar: 'Abandonaram',
  clicaram_sem_pagar: 'Clicaram sem pagar',
  pagamento_recusado: 'Pagamento recusado',
  responderam_sem_fechar: 'Responderam sem fechar',
  expirados_d15: 'Expirados D+15',
  expirados_d30: 'Expirados D+30',
  expirados_d60: 'Expirados D+60',
};

export const SEGMENT_FAMILY_LABEL: Readonly<Record<SegmentFamily, string>> = {
  carrinho: 'Carrinho abandonado',
  comercial: 'Comercial',
  expirados: 'Expirados',
};

export const SEGMENT_FAMILY_ICON: Readonly<Record<SegmentFamily, IconName>> = {
  carrinho: 'carrinho',
  comercial: 'usuarios',
  expirados: 'calendario',
};

export const SEGMENT_FAMILY_ORDER: readonly SegmentFamily[] = ['carrinho', 'comercial', 'expirados'];

export const BROADCAST_STATUS_LABEL: Readonly<Record<BroadcastStatus, string>> = {
  agendada: 'Agendada',
  em_andamento: 'Em andamento',
  pausada: 'Pausada',
  concluida: 'Concluída',
};

export const BROADCAST_STATUS_TONE: Readonly<Record<BroadcastStatus, BadgeTone>> = {
  agendada: 'neutral',
  em_andamento: 'info',
  pausada: 'warning',
  concluida: 'good',
};

export const PAUSED_REASON_LABEL: Readonly<Record<PausedReason, string>> = {
  manual: 'pausado pela equipe',
  optout_alto: 'pausado pela trava de opt-out',
};

export const TEMPLATE_CATEGORY_LABEL: Readonly<Record<TemplateCategory, string>> = {
  utility: 'Utility',
  marketing: 'Marketing',
};

export const TEMPLATE_CATEGORY_TONE: Readonly<Record<TemplateCategory, BadgeTone>> = {
  utility: 'info',
  marketing: 'accent',
};

export const TEMPLATE_SECTOR_LABEL: Readonly<Record<TemplateSector, string>> = {
  comercial: 'Comercial',
  funil: 'Funil',
  suporte: 'Suporte',
};

export const TEMPLATE_APPROVAL_LABEL: Readonly<Record<TemplateApproval, string>> = {
  aprovado: 'Aprovado na Meta',
  em_analise: 'Em análise na Meta',
  texto_livre: 'Texto livre (Evolution)',
};

export const TEMPLATE_APPROVAL_TONE: Readonly<Record<TemplateApproval, BadgeTone>> = {
  aprovado: 'good',
  em_analise: 'warning',
  texto_livre: 'neutral',
};

export const SENDER_KIND_LABEL: Readonly<Record<SenderKind, string>> = {
  evolution: 'Número do comercial (Evolution)',
  oficial: 'API oficial da Meta',
};

export const SENDER_KIND_SHORT: Readonly<Record<SenderKind, string>> = {
  evolution: 'Evolution',
  oficial: 'API oficial',
};

export const NUMBER_QUALITY_LABEL: Readonly<Record<NumberQuality, string>> = {
  alta: 'Qualidade alta',
  media: 'Qualidade média',
  baixa: 'Qualidade baixa',
};

export const NUMBER_QUALITY_TONE: Readonly<Record<NumberQuality, BadgeTone>> = {
  alta: 'good',
  media: 'warning',
  baixa: 'critical',
};

export const OPTOUT_REASON_LABEL: Readonly<Record<OptOutReason, string>> = {
  pediu_sair: 'Respondeu SAIR',
  bloqueou_numero: 'Bloqueou o número',
  manual: 'Bloqueio manual da equipe',
};

export const EMAIL_STATUS_LABEL: Readonly<Record<EmailCampaignStatus, string>> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  enviando: 'Enviando',
  concluida: 'Concluída',
};

export const EMAIL_STATUS_TONE: Readonly<Record<EmailCampaignStatus, BadgeTone>> = {
  rascunho: 'neutral',
  agendada: 'accent',
  enviando: 'info',
  concluida: 'good',
};

export const EMAIL_PROVIDER_LABEL: Readonly<Record<EmailProviderState, string>> = {
  a_definir: 'Provedor a definir',
  operacional: 'Motor em dia',
};

export const DNS_CHECK_LABEL: Readonly<Record<DnsCheck, string>> = {
  ok: 'ok',
  aviso: 'atenção',
  falha: 'falhou',
};

export const DNS_CHECK_TONE: Readonly<Record<DnsCheck, BadgeTone>> = {
  ok: 'good',
  aviso: 'warning',
  falha: 'critical',
};

/**
 * O que um estorno faz com a comissao, pela politica DA EMPRESA
 * (`companies.refund_policy`, emenda E2 do ADR-001). Ausente = padrao
 * `reverte_chargeback`. Copia deliberada do texto do EditorDeComissao: um
 * import cruzado de modulo quebraria este quando o outro mudasse.
 */
export const REFUND_POLICY_TEXT: Readonly<Record<RefundPolicy, string>> = {
  nao_reverte: 'Nesta empresa nenhum estorno desfaz a comissão: nem reembolso, nem chargeback.',
  reverte_chargeback: 'Nesta empresa o reembolso não desfaz a comissão; o chargeback desfaz.',
  reverte_tudo: 'Nesta empresa qualquer estorno total desfaz a comissão: reembolso ou chargeback. Reembolso parcial não desfaz.',
};

/** Ultimos 6 caracteres do id, com `#`: o operador cita "#a3f9c1" no suporte. */
export function idCurto(id: string): string {
  return `#${id.slice(-6)}`;
}

/**
 * Mascara e-mail preservando a primeira letra e o dominio: `c*****@dominio`.
 * Mesma razao de `maskPhoneDisplay`: a lista de descadastrados fica aberta
 * numa tela de escritorio e ninguem precisa do endereco inteiro para
 * reconhecer um caso.
 */
export function mascararEmail(email: string): string {
  const arroba = email.indexOf('@');
  if (arroba <= 0) {
    return '***';
  }
  const usuario = email.slice(0, arroba);
  const dominio = email.slice(arroba + 1);
  return `${usuario.charAt(0)}${'*'.repeat(Math.max(3, usuario.length - 1))}@${dominio}`;
}

/** Sem acento e em minusculas: "reativacao" encontra "Reativação". */
export function normalizarBusca(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
